/**
 * Resumen de faltantes de stock por SKU (demanda total vs disponible SAP) para los Excel de
 * «Exportar validación» (carga mayorista y «Procesar seleccionados»).
 */
import { bulkPreviewRowHasStockShortage } from './bulkPreviewExportEligibility';

const FILL_WARNING = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFFFF9C4' }
};

/** Nombre de la hoja agregada a los Excel marcados. */
export const STOCK_SHORTAGE_SHEET_NAME = 'Resumen stock';

/**
 * Agrupa por SKU las filas con faltante. El pedido total se toma de `skuTotalDemand` (enrich) y, si falta,
 * de la suma de las líneas del SKU con faltante.
 *
 * @param {object[]} rows
 * @returns {{ sku: string, descripcion: string, pedidoTotal: number, disponible: number, faltante: number, sucursales: number }[]}
 *   ordenado por faltante descendente y luego SKU.
 */
export function buildStockShortageSummary(rows) {
  const bySku = new Map();
  if (!Array.isArray(rows)) return [];
  for (const r of rows) {
    if (!r || !bulkPreviewRowHasStockShortage(r)) continue;
    const sku = String(r.itemCodeSap || r.skuIac || '').trim();
    if (!sku) continue;
    const key = sku.toUpperCase();
    const acc = bySku.get(key) || {
      sku,
      descripcion: '',
      sumLines: 0,
      skuTotalDemand: null,
      disponible: null,
      branches: new Set(),
      skuBranchCount: null
    };
    if (!acc.descripcion && r.descripcion) acc.descripcion = String(r.descripcion);
    const q = Number(r.cantOc);
    if (Number.isFinite(q) && q > 0) acc.sumLines += q;
    const td = Number(r.skuTotalDemand);
    if (r.skuTotalDemand != null && Number.isFinite(td)) acc.skuTotalDemand = td;
    const bc = Number(r.skuBranchCount);
    if (r.skuBranchCount != null && Number.isFinite(bc)) acc.skuBranchCount = bc;
    const stk = Number(r.stockDispSap);
    if (r.stockDispSap != null && Number.isFinite(stk)) acc.disponible = stk;
    const bk = String(r.sucursalDestinoLinea || r.tiendaDestino || r._orderNumber || '').trim();
    if (bk) acc.branches.add(bk);
    bySku.set(key, acc);
  }
  const out = [];
  for (const acc of bySku.values()) {
    const pedidoTotal = acc.skuTotalDemand != null ? acc.skuTotalDemand : acc.sumLines;
    const disponible = acc.disponible != null ? acc.disponible : 0;
    out.push({
      sku: acc.sku,
      descripcion: acc.descripcion,
      pedidoTotal,
      disponible,
      faltante: pedidoTotal - Math.max(0, disponible),
      sucursales: acc.skuBranchCount != null ? acc.skuBranchCount : acc.branches.size
    });
  }
  out.sort((a, b) => b.faltante - a.faltante || a.sku.localeCompare(b.sku));
  return out;
}

/**
 * Agrega la hoja «Resumen stock» al workbook si hay faltantes. No hace nada con la lista vacía.
 *
 * @param {import('exceljs').Workbook} wb
 * @param {ReturnType<typeof buildStockShortageSummary>} summary
 * @param {{ sku?: string, descripcion?: string, pedidoTotal?: string, disponible?: string, faltante?: string, sucursales?: string }} [labels]
 * @returns {boolean} true si se agregó la hoja
 */
export function addStockShortageSheet(wb, summary, labels = {}) {
  if (!wb || !Array.isArray(summary) || !summary.length) return false;
  const ws = wb.addWorksheet(STOCK_SHORTAGE_SHEET_NAME, {
    views: [{ state: 'frozen', ySplit: 1 }]
  });
  const headers = [
    labels.sku || 'SKU',
    labels.descripcion || 'Descripción',
    labels.pedidoTotal || 'Pedido total',
    labels.disponible || 'Disponible SAP',
    labels.faltante || 'Faltante',
    labels.sucursales || 'N° sucursales'
  ];
  const head = ws.getRow(1);
  headers.forEach((h, i) => {
    const c = head.getCell(i + 1);
    c.value = h;
    c.font = { bold: true };
  });
  summary.forEach((s, i) => {
    const row = ws.getRow(i + 2);
    const skuCell = row.getCell(1);
    skuCell.value = s.sku;
    skuCell.numFmt = '@';
    row.getCell(2).value = s.descripcion || '';
    row.getCell(3).value = s.pedidoTotal;
    row.getCell(4).value = s.disponible;
    const f = row.getCell(5);
    f.value = s.faltante;
    f.font = { bold: true };
    row.getCell(6).value = s.sucursales;
    for (let c = 1; c <= headers.length; c += 1) row.getCell(c).fill = FILL_WARNING;
  });
  const widths = [20, 42, 14, 16, 12, 15];
  widths.forEach((w, i) => {
    ws.getColumn(i + 1).width = w;
  });
  return true;
}
